import { useState, useCallback, useRef } from 'react';
import friendlyError from '../../utils/friendlyError';
import {
  createDigitalProfile, batchCreateProfiles,
  verifyProfileCredentials, getProfileStatus,
  autoRegisterProfile,
} from '../../services/digitalProfilesService';
import { PLATFORMS } from './CreadorPerfilesStyles';
import { generateRandomProfiles } from './randomProfileFactory';

function parseCSV(text) {
  const lines = text.split(/\r?\n/).filter((l) => l.trim());
  if (lines.length < 2) return [];
  const headers = lines[0].split(/[;,]/).map((h) => h.trim().toLowerCase());
  const pIdx = headers.findIndex((h) => ['plataforma', 'platform', 'red'].includes(h));
  const uIdx = headers.findIndex((h) => ['usuario', 'username', 'email', 'correo'].includes(h));
  const passIdx = headers.findIndex((h) => ['password', 'contrasena', 'contraseña', 'clave'].includes(h));
  const proxyIdx = headers.findIndex((h) => h === 'proxy');
  const ckIdx = headers.findIndex((h) => h === 'cookies');
  if (pIdx === -1 || uIdx === -1) return [];
  return lines.slice(1).map((line) => {
    const cols = line.split(/[;,]/).map((c) => c.trim());
    return {
      platform: cols[pIdx] || '',
      username: cols[uIdx] || '',
      password: passIdx >= 0 ? cols[passIdx] : '',
      proxy_dedicado: proxyIdx >= 0 ? cols[proxyIdx] : '',
      cookies_raw: ckIdx >= 0 ? cols[ckIdx] : '',
    };
  }).filter((a) => a.platform && a.username);
}

export default function useCreadorPerfiles() {
  const [platform, setPlatform] = useState('x');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [cookies, setCookies] = useState('');
  const [proxy, setProxy] = useState('');
  const [promptPersonalidad, setPromptPersonalidad] = useState('');
  const [intereses, setIntereses] = useState([]);
  const [frecuenciaPosteo, setFrecuenciaPosteo] = useState('medio');
  const [tasaInteraccion, setTasaInteraccion] = useState(30);
  const [maxLikes, setMaxLikes] = useState(30);
  const [maxPosts, setMaxPosts] = useState(5);
  const [zonaHoraria, setZonaHoraria] = useState('America/Argentina/Buenos_Aires');
  const [horaInicioSueno, setHoraInicioSueno] = useState('23:00');
  const [horaFinSueno, setHoraFinSueno] = useState('07:00');
  const [saving, setSaving] = useState(false);
  const [savedProfile, setSavedProfile] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const [batchAccounts, setBatchAccounts] = useState([]);
  const [batchResult, setBatchResult] = useState(null);
  const [batchBusy, setBatchBusy] = useState(false);
  const fileRef = useRef(null);

  // Generador automatico aleatorio (lote de N perfiles ficticios).
  const [randomPlatform, setRandomPlatform] = useState('x');
  const [randomCount, setRandomCount] = useState(5);
  const [randomSeed, setRandomSeed] = useState('');
  const [randomApplyBase, setRandomApplyBase] = useState(true);
  // Opciones avanzadas de generación
  const [randomGender, setRandomGender] = useState('mixed'); // 'mixed' | 'M' | 'F' | 'U'
  const [randomCountry, setRandomCountry] = useState('any'); // 'any' | pais específico
  const [randomEmailDomain, setRandomEmailDomain] = useState('adbizetesting.com.ar');
  const [randomBusy, setRandomBusy] = useState(false);
  const [randomResult, setRandomResult] = useState(null);
  // Estado para el modal de preview
  const [previewProfiles, setPreviewProfiles] = useState([]);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewMode, setPreviewMode] = useState('generate'); // 'generate' | 'confirm'
  const [autoRegBusy, setAutoRegBusy] = useState(false);
  const [autoRegResult, setAutoRegResult] = useState(null);
  const [autoRegSteps, setAutoRegSteps] = useState([]);
  const [autoRegStepStatus, setAutoRegStepStatus] = useState({});

  const REG_STEPS = [
    { key: 'email', label: 'Creando email temporal' },
    { key: 'navigate', label: 'Navegando a X / signup' },
    { key: 'form', label: 'Completando formulario (nombre, email, fecha)' },
    { key: 'captcha', label: 'Detectando / resolviendo captcha' },
    { key: 'phone', label: 'Verificación por teléfono (si aplica)' },
    { key: 'verify_email', label: 'Verificando email con código' },
    { key: 'password', label: 'Configurando contraseña y username' },
    { key: 'cookies', label: 'Extrayendo cookies de sesión' },
    { key: 'save', label: 'Guardando cuenta en la base de datos' },
  ];

  function animateSteps(stepKeys, durationMs = 120000) {
    const stepOrder = REG_STEPS.map((s) => s.key);
    const indices = stepKeys.map((k) => stepOrder.indexOf(k)).filter((i) => i >= 0).sort((a, b) => a - b);
    if (indices.length === 0) return;
    const delayPerStep = Math.max(800, Math.floor(durationMs / indices.length));
    indices.forEach((idx, i) => {
      const key = stepOrder[idx];
      setTimeout(() => {
        setAutoRegStepStatus((prev) => ({ ...prev, [key]: 'running' }));
      }, i * delayPerStep);
      setTimeout(() => {
        setAutoRegStepStatus((prev) => ({ ...prev, [key]: 'done' }));
      }, (i + 1) * delayPerStep - 200);
    });
  }

  const platformMeta = PLATFORMS.find((p) => p.id === platform) || PLATFORMS[0];

  const toggleInteres = useCallback((tag) => {
    setIntereses((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]
    );
  }, []);

  const resetForm = () => {
    setUsername(''); setPassword(''); setCookies(''); setProxy('');
    setPromptPersonalidad(''); setIntereses([]);
    setFrecuenciaPosteo('medio'); setTasaInteraccion(30);
    setMaxLikes(30); setMaxPosts(5); setFeedback(null);
  };

  // Helper: dado un payload completo, lo postea y dispara la verificacion.
  // Aislado del estado del form para que tanto el flujo manual (lee del state)
  // como el flujo "generar y crear" (acaba de sortear datos) lo puedan usar
  // sin chocarse con el async de setState de React.
  //
  // Si el payload no trae password ni cookies, saltea el verify: no hay nada
  // para validar contra la plataforma. El perfil queda guardado y el feedback
  // le dice al usuario que pegue credenciales reales para activarlo (asi no
  // aparece un "Fallo" enganoso despues de cada "Generar todo + Crear").
  const saveAndVerifyPayload = async (payload) => {
    setSaving(true); setFeedback(null);
    try {
      const result = await createDigitalProfile(payload);
      setSavedProfile(result);

      const hasCreds = (payload.password && String(payload.password).trim())
        || (payload.cookies_raw && String(payload.cookies_raw).trim());

      if (!hasCreds) {
        setFeedback({
          type: 'success',
          message: 'Perfil generado. Pegale cookies o sessionid en Perfiles Digitales para que pueda loguearse en la plataforma.',
        });
        return result;
      }

      setFeedback({ type: 'success', message: 'Perfil guardado. Verificando credenciales...' });
      if (result.id) {
        try { await verifyProfileCredentials(result.id); } catch {}
        setTimeout(async () => {
          try {
            const status = await getProfileStatus(result.id);
            if (status.account) {
              setSavedProfile(status.account);
              setFeedback(status.account.status === 'active'
                ? { type: 'success', message: 'Verificacion exitosa. La cuenta esta activa y lista.' }
                : { type: 'error', message: 'Estado: ' + status.account.status + '. ' + (status.account.last_error || '') }
              );
            }
          } catch {}
        }, 8000);
      }
      return result;
    } catch (err) {
      setFeedback({ type: 'error', message: friendlyError(err, 'Error al guardar.') });
      return null;
    } finally { setSaving(false); }
  };

  const handleSaveAndVerify = async () => {
    if (!username.trim()) {
      setFeedback({ type: 'error', message: 'El nombre de usuario es obligatorio.' });
      return;
    }
    await saveAndVerifyPayload({
      platform, username: username.trim(),
      password: password || null,
      cookies_raw: cookies || null,
      proxy_dedicado: proxy || null,
      prompt_personalidad: promptPersonalidad || null,
      intereses,
      frecuencia_posteo: frecuenciaPosteo,
      tasa_interaccion: tasaInteraccion,
      max_likes_diarios: maxLikes,
      max_posts_diarios: maxPosts,
      zona_horaria: zonaHoraria,
      hora_inicio_sueno: horaInicioSueno,
      hora_fin_sueno: horaFinSueno,
    });
  };

  // Genera UN perfil aleatorio: rellena visualmente todos los campos del
  // formulario y dispara el flujo de guardar + verificar credenciales.
  // El payload se pasa directo al helper para evitar la trampa del setState
  // async (sino handleSaveAndVerify leeria el state viejo).
  const handleGenerateAndCreate = async () => {
    const acc = generateRandomProfiles({
      platform, count: 1, base: {}, seed: undefined,
    })[0];
    // Reflejo en la UI: el usuario ve los campos rellenados.
    setUsername(acc.username);
    setPassword(acc.password || '');
    setCookies(acc.cookies_raw || '');
    setProxy(acc.proxy_dedicado || '');
    setPromptPersonalidad(acc.prompt_personalidad);
    setIntereses(acc.intereses);
    setFrecuenciaPosteo(acc.frecuencia_posteo);
    setTasaInteraccion(acc.tasa_interaccion);
    setMaxLikes(acc.max_likes_diarios);
    setMaxPosts(acc.max_posts_diarios);
    setZonaHoraria(acc.zona_horaria);
    setHoraInicioSueno(acc.hora_inicio_sueno);
    setHoraFinSueno(acc.hora_fin_sueno);
    await saveAndVerifyPayload({
      platform,
      username: acc.username,
      password: acc.password || null,
      cookies_raw: acc.cookies_raw || null,
      proxy_dedicado: acc.proxy_dedicado || null,
      prompt_personalidad: acc.prompt_personalidad,
      intereses: acc.intereses,
      frecuencia_posteo: acc.frecuencia_posteo,
      tasa_interaccion: acc.tasa_interaccion,
      max_likes_diarios: acc.max_likes_diarios,
      max_posts_diarios: acc.max_posts_diarios,
      zona_horaria: acc.zona_horaria,
      hora_inicio_sueno: acc.hora_inicio_sueno,
      hora_fin_sueno: acc.hora_fin_sueno,
    });
  };

  // Paso 1: Generar perfiles para preview (sin guardar aún)
  const handleGeneratePreview = async () => {
    setRandomBusy(true);
    setRandomResult(null);
    try {
      const base = randomApplyBase ? {
        proxy_dedicado: proxy || '',
        cookies_raw: cookies || '',
        prompt_personalidad: promptPersonalidad || '',
        intereses: intereses.length ? intereses : null,
        frecuencia_posteo: frecuenciaPosteo,
        tasa_interaccion: tasaInteraccion,
        max_likes_diarios: maxLikes,
        max_posts_diarios: maxPosts,
        zona_horaria: zonaHoraria,
        hora_inicio_sueno: horaInicioSueno,
        hora_fin_sueno: horaFinSueno,
      } : {};
      
      // Agregar opciones avanzadas al base
      if (randomGender !== 'mixed') {
        base.gender = randomGender;
      }
      if (randomCountry !== 'any') {
        base.pais = randomCountry;
      }
      // Dominio de email (catch-all propio o generic)
      base.emailDomain = randomEmailDomain || null;
      
      // Solo generar, NO guardar todavía
      const accounts = generateRandomProfiles({
        platform: randomPlatform,
        count: randomCount,
        base,
        seed: randomSeed || undefined,
      });
      
      // Guardar en estado para el preview
      setPreviewProfiles(accounts);
      setPreviewOpen(true);
      setPreviewMode('generate');
    } catch (err) {
      setFeedback({ 
        type: 'error', 
        message: err?.message || 'Error generando preview de perfiles' 
      });
    } finally {
      setRandomBusy(false);
    }
  };

  // Paso 2: Confirmar creación de los perfiles del preview
  const handleConfirmCreateFromPreview = async () => {
    if (previewProfiles.length === 0) return;
    
    setRandomBusy(true);
    setPreviewOpen(false);
    
    try {
      // Limpiar _meta antes de enviar al backend
      const accountsToCreate = previewProfiles.map(({ _meta, ...rest }) => rest);
      const result = await batchCreateProfiles(accountsToCreate);
      setRandomResult({ ...result, accounts: previewProfiles });
      setPreviewProfiles([]);
      setFeedback({ 
        type: 'success', 
        message: `Creados: ${result.created || 0}, Actualizados: ${result.updated || 0}, Errores: ${result.errors?.length || 0}` 
      });
    } catch (err) {
      setRandomResult({
        errors: [{ index: 0, error: friendlyError(err, 'Error creando perfiles') }],
      });
      setFeedback({ type: 'error', message: err?.message || 'Error al crear perfiles' });
    } finally {
      setRandomBusy(false);
    }
  };

  // Función legacy - ahora se usa el flujo de dos pasos
  const handleGenerateRandom = handleGeneratePreview;

  // Auto-registro de cuentas en X via Playwright + email temporal
  const handleAutoRegister = async (count = 1) => {
    setAutoRegBusy(true);
    setAutoRegResult(null);
    setAutoRegSteps(REG_STEPS);
    setAutoRegStepStatus({});
    setFeedback({ type: 'success', message: `Registrando ${count} cuenta(s) en X automáticamente... esto puede tardar ~2 min por cuenta.` });
    try {
      animateSteps(['email', 'navigate', 'form', 'captcha', 'phone', 'verify_email', 'password', 'cookies', 'save'], 140000);
      const result = await autoRegisterProfile({ platform: 'x', count });
      setAutoRegResult(result);
      // Marcar pasos finales según resultado real
      setAutoRegStepStatus((prev) => ({
        ...prev,
        email: 'done', navigate: 'done', form: 'done',
        ...(result.ok ? {
          captcha: 'done', phone: 'done', verify_email: 'done', password: 'done', cookies: 'done', save: 'done',
        } : {
          ...(result.step === 'captcha_or_challenge' ? { captcha: 'error' } : { captcha: 'done' }),
          ...(result.step === 'email' ? { email: 'error', navigate: 'pending', form: 'pending' } : {}),
          ...(result.step === 'unknown' ? { cookies: 'error', save: 'error' } : {}),
        }),
      }));
      if (result.ok) {
        setFeedback({ type: 'success', message: `Auto-registro completado: ${result.created} creada(s), ${result.failed} fallida(s).` });
      } else {
        setFeedback({ type: 'error', message: result.results?.[0]?.error || 'Auto-registro falló.' });
      }
    } catch (err) {
      setAutoRegResult({ ok: false, error: friendlyError(err) });
      setAutoRegStepStatus((prev) => ({ ...prev, save: 'error' }));
      setFeedback({ type: 'error', message: friendlyError(err, 'Error en auto-registro.') });
    } finally {
      setAutoRegBusy(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => { setBatchAccounts(parseCSV(ev.target.result)); };
    reader.readAsText(file);
    e.target.value = '';
  };

  const handleBatchUpload = async () => {
    if (batchAccounts.length === 0) return;
    setBatchBusy(true); setBatchResult(null);
    try {
      const enriched = batchAccounts.map((a) => ({
        ...a,
        prompt_personalidad: promptPersonalidad || null,
        intereses,
        frecuencia_posteo: frecuenciaPosteo,
        tasa_interaccion: tasaInteraccion,
        max_likes_diarios: maxLikes,
        max_posts_diarios: maxPosts,
        zona_horaria: zonaHoraria,
        hora_inicio_sueno: horaInicioSueno,
        hora_fin_sueno: horaFinSueno,
      }));
      setBatchResult(await batchCreateProfiles(enriched));
    } catch (err) {
      setBatchResult({ errors: [{ index: 0, error: friendlyError(err) }] });
    } finally { setBatchBusy(false); }
  };

  return {
    platform, setPlatform,
    username, setUsername,
    password, setPassword,
    cookies, setCookies,
    proxy, setProxy,
    promptPersonalidad, setPromptPersonalidad,
    intereses, toggleInteres,
    frecuenciaPosteo, setFrecuenciaPosteo,
    tasaInteraccion, setTasaInteraccion,
    maxLikes, setMaxLikes,
    maxPosts, setMaxPosts,
    zonaHoraria, setZonaHoraria,
    horaInicioSueno, setHoraInicioSueno,
    horaFinSueno, setHoraFinSueno,
    saving,
    savedProfile, setSavedProfile,
    feedback, setFeedback,
    batchAccounts, batchResult, batchBusy,
    fileRef,
    platformMeta,
    resetForm,
    handleSaveAndVerify,
    handleGenerateAndCreate,
    handleFileSelect,
    handleBatchUpload,
    // Generador aleatorio
    randomPlatform, setRandomPlatform,
    randomCount, setRandomCount,
    randomSeed, setRandomSeed,
    randomApplyBase, setRandomApplyBase,
    // Opciones avanzadas
    randomGender, setRandomGender,
    randomCountry, setRandomCountry,
    randomEmailDomain, setRandomEmailDomain,
    randomBusy, randomResult,
    handleGenerateRandom,
    // Preview modal
    previewProfiles,
    previewOpen, setPreviewOpen,
    handleGeneratePreview,
    handleConfirmCreateFromPreview,
    // Auto-registro
    autoRegBusy, autoRegResult,
    handleAutoRegister,
    autoRegSteps, autoRegStepStatus,
  };
}
